package com.meistercharts.charts.pv.modulePlanning

import com.meistercharts.canvas.interaction.ElementVisualState
import com.meistercharts.canvas.interaction.HandleRole
import com.meistercharts.canvas.interaction.InteractionState
import com.meistercharts.charts.pv.roofPlanning.Clickable
import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.charts.pv.roofPlanning.ModuleArea
import com.meistercharts.charts.pv.roofPlanning.UnusableArea
import com.meistercharts.charts.pv.roofPlanning.belongsToSameElementAs

/**
 * Projects the interaction state of the module planning onto the vocabulary the painters understand.
 *
 * This is the only place that knows both sides. Painters take an [ElementVisualState] and never see the interaction
 * state; a new interaction state changes this file and nothing else.
 */

/**
 * Returns how the given module area is painted.
 */
fun InteractionState<Clickable>.visualStateOf(moduleArea: ModuleArea): ElementVisualState {
  return visualStateOf(moduleArea as Clickable)
}

/**
 * Returns how the given unusable area is painted *on top of* the roof.
 *
 * [ElementVisualState.Normal] means the module planning adds nothing - the unusable area painted by the roof layer below
 * stays as it is.
 */
fun InteractionState<Clickable>.visualStateOf(unusableArea: UnusableArea): ElementVisualState {
  return visualStateOf(unusableArea as Clickable)
}

/**
 * Returns how the given module is highlighted on top of the roof.
 *
 * A module that overlaps another one is flagged in every state, except while the user is about to delete or restore it -
 * then the pending action wins, because that is what the click will do.
 */
fun InteractionState<Clickable>.highlightOf(module: Module, overlapping: Boolean): ModuleHighlight {
  if (armsDeleteOf(module)) {
    return if (module.deleted) ModuleHighlight.UndeleteArmed else ModuleHighlight.DeleteArmed
  }

  if (module.deleted.not() && overlapping) {
    return ModuleHighlight.Overlapping
  }

  if (draggedElement.belongsToSameElementAs(module)) {
    return ModuleHighlight.Dragging
  }

  if (hoveredElement.belongsToSameElementAs(module)) {
    return ModuleHighlight.Hover
  }

  return ModuleHighlight.None
}

/**
 * Returns which icon is painted in the center of the given module.
 */
fun InteractionState<Clickable>.iconOf(module: Module): ModuleIcon {
  if (armsDeleteOf(module)) {
    return if (module.deleted) ModuleIcon.UndeleteArmed else ModuleIcon.DeleteArmed
  }

  //The delete button is offered on the exact module under the mouse, not on its neighbours
  if (hoveredElement == module && armedRole == HandleRole.Body) {
    return ModuleIcon.Delete
  }

  return ModuleIcon.None
}

/**
 * Returns how the given element is painted, from the point of view of the module planning overlay.
 */
private fun InteractionState<Clickable>.visualStateOf(element: Clickable): ElementVisualState {
  if (draggedElement.belongsToSameElementAs(element)) {
    return ElementVisualState.Dragging
  }

  if (hoveredElement.belongsToSameElementAs(element)) {
    when (armedRole) {
      HandleRole.DeleteButton -> return ElementVisualState.DeleteArmed
      //Only a module area can be rotated - a rotate armed elsewhere leaves this element alone
      HandleRole.RotateButton -> if (element is ModuleArea) return ElementVisualState.RotateArmed

      HandleRole.Body, HandleRole.AddButton, HandleRole.ResizeHandle, null -> {}
    }

    return if (isSelected(element)) ElementVisualState.SelectedHovering else ElementVisualState.Hover
  }

  return if (isSelected(element)) ElementVisualState.Selected else ElementVisualState.Normal
}

/**
 * Returns true if releasing the mouse right now deletes the given element.
 */
private fun InteractionState<Clickable>.armsDeleteOf(element: Clickable): Boolean {
  return armedRole == HandleRole.DeleteButton && hoveredElement == element
}

/**
 * Returns true if the given element is part of the selection.
 */
private fun InteractionState<Clickable>.isSelected(element: Clickable): Boolean {
  return selected.belongsToSameElementAs(element)
}
